/**
 * Dateien, Dokumente und Wissen am Einsatz — `PWA-005`.
 *
 * `07` §7.7 beschreibt den Bereich „Dateien" als **auftragsbezogene Sicht
 * statt technischer Ordnerstruktur**. Ein Monteur sucht nicht in
 * `/Auftraege/2481/Unterlagen`, er sucht die Unterlage zu dem, wovor er steht.
 * Deshalb löst diese Ansicht über den Link-Graphen auf und zeigt keinen Baum.
 *
 * **Die Offlinekennzeichnung ist der Kern, nicht die Zierde.** `07` §7.9
 * unterscheidet vorgeladen, auf Wunsch und nur online — und verlangt, dass die
 * App zeigt, wenn ein Inhalt **nicht** vorgeladen wurde. Im Funkloch ist das
 * der Unterschied zwischen „ich hab's dabei" und einer vergeblichen Fahrt.
 *
 * Wissen erscheint **kontextbezogen** (`07` §7.11): Fehlercode zum Gerät,
 * Wartungsanleitung, Sicherheitsanweisung. Die Zuordnung läuft hier über
 * Schlagworte gegen den Auftragstitel — im Zielbild über die
 * Berechtigungsprüfung und später die semantische Suche aus Phase 8.
 */

import { useMemo } from "react";

import { dienstadresse, quelladresse, systemname, findeAblage, findeAuftrag, nachbarn } from "@hwc/demo-data";
import type { Ablageobjekt, Einsatz } from "@hwc/demo-data";

import { useMonteur } from "../../src/Sitzung.js";
import { groesse } from "../../src/bausteine.js";

const OFFLINETEXT: Record<NonNullable<Ablageobjekt["offline"]>, string> = {
  vorgeladen: "offline verfügbar",
  "auf-wunsch": "nur mit Netz laden",
  "nur-online": "nur online",
};

const OFFLINETON: Record<NonNullable<Ablageobjekt["offline"]>, string> = {
  vorgeladen: "erfolg",
  "auf-wunsch": "neutral",
  "nur-online": "warnung",
};

export function Ressourcen({ einsatz }: { einsatz: Einsatz }) {
  const { bestand, online } = useMonteur();

  const auftrag = findeAuftrag(bestand, einsatz.auftrag);

  const verknuepft = useMemo(() => {
    if (!auftrag) return [];
    return nachbarn(bestand, auftrag.ref)
      .map((r) => findeAblage(bestand, r))
      .filter((o): o is Ablageobjekt => o !== undefined);
  }, [bestand, auftrag]);

  const dateien = verknuepft.filter((o) => o.art !== "wissen");

  /**
   * Passende Wissensseiten.
   *
   * Über Schlagworte gegen Titel und Beschreibung des Auftrags. Das ist grob
   * und soll es sein — was hier zu beurteilen ist, ist ob kontextbezogenes
   * Wissen an dieser Stelle überhaupt hilft, nicht wie gut ein Treffer ist.
   * Die richtige Zuordnung macht später `AI-002` mit Berechtigungsprüfung.
   */
  const wissen = useMemo(() => {
    const text = `${einsatz.titel} ${auftrag?.titel ?? ""} ${auftrag?.beschreibung ?? ""} `
      + `${auftrag?.gewerk ?? ""} ${einsatz.hinweis ?? ""}`;
    const klein = text.toLowerCase();
    return bestand.ablage.filter(
      (o) =>
        o.art === "wissen"
        && (o.schlagworte ?? []).some((s) => klein.includes(s.toLowerCase())),
    );
  }, [bestand, einsatz, auftrag]);

  return (
    <>
      <h2>Unterlagen</h2>
      {dateien.length === 0 ? (
        <p className="leer leise">Zu diesem Auftrag ist nichts verknüpft.</p>
      ) : (
        <ul className="postenliste">
          {dateien.map((o) => {
            const erreichbar = online || o.offline === "vorgeladen";
            /*
             * **Der Weg ins Quellsystem — nur mit Netz und nur mit Adresse.**
             *
             * `PWA-005` verlangt „vereinheitlichte Anzeige mit Quellhinweis,
             * Offline-Markierung und ACL-Prüfung". Die Markierung stand hier
             * schon, der Quellhinweis fehlte, und öffnen ließ sich nichts —
             * ein Monteur sah, dass es den Schaltplan gibt, und kam nicht
             * heran.
             *
             * Kein Rahmen, anders als im Portal: `07` §7.14 will
             * Einhandbedienung, und ein gerahmtes Nextcloud auf 375 Pixeln ist
             * keine. Der Verweis öffnet den Dienst; zurück kommt man über den
             * Zurück-Knopf.
             *
             * **Im Funkloch erscheint er nicht.** Ein Verweis, der nur mit
             * Netz trägt, gehört nicht neben eine Zeile, die gerade
             * „jetzt nicht erreichbar" sagt — `07` §7.9 verlangt, dass die App
             * den Unterschied zeigt, statt ihn zu verwischen.
             */
            const ziel = erreichbar && dienstadresse(o.ref)
              ? quelladresse(o.ref, {
                  name: o.name,
                  ...(o.pfad ? { pfad: o.pfad } : {}),
                })
              : undefined;

            return (
              <li key={o.ref.opaqueId} className={erreichbar ? "" : "postenliste__fern"}>
                <span>
                  {ziel ? (
                    <a href={ziel} target="_blank" rel="noopener noreferrer">
                      {o.name}
                    </a>
                  ) : (
                    o.name
                  )}
                  <span className={`abzeichen abzeichen--${OFFLINETON[o.offline ?? "nur-online"]}`}>
                    {OFFLINETEXT[o.offline ?? "nur-online"]}
                  </span>
                  {/*
                    Der Quellhinweis aus `PWA-005`. Klein und hinter der
                    Offlinemarkierung: `07` §7.12 nennt die technische Herkunft
                    ausdrücklich einen **sekundären** Hinweis — vorn steht, was
                    die Datei ist, nicht wo sie liegt.
                  */}
                  <span className="leise"> · {systemname(o.ref)}</span>
                  {!erreichbar && (
                    <span className="leise"> — jetzt nicht erreichbar</span>
                  )}
                </span>
                <span>{groesse(o.groesseBytes)}</span>
              </li>
            );
          })}
        </ul>
      )}

      {wissen.length > 0 && (
        <>
          <h2>Passendes Wissen</h2>
          <ul className="postenliste">
            {wissen.map((o) => (
              <li key={o.ref.opaqueId}>
                <span>
                  {o.name}
                  <span className={`abzeichen abzeichen--${OFFLINETON[o.offline ?? "nur-online"]}`}>
                    {OFFLINETEXT[o.offline ?? "nur-online"]}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          {/*
            Der Inhalt der ersten passenden Seite steht direkt darunter. Ein
            Monteur auf einer Leiter tippt nicht erst auf einen Eintrag, um
            dann zu lesen — und die Seiten sind kurz genug, dass das geht.
          */}
          {wissen[0]?.inhalt && (
            <pre className="wissenstext">{wissen[0].inhalt}</pre>
          )}
        </>
      )}

      <p className="leise fussnote">
        Große Handbücher werden nach <code>07</code> §7.9 nur auf Wunsch offline
        gespeichert. Was hier „nur online" trägt, ist im Funkloch nicht da — vor der
        Abfahrt öffnen, wenn es gebraucht wird.
      </p>
    </>
  );
}
